import type { ReportedMonitorSummary, ReportedRunRecord } from '@simplemon/lib';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { JobNotFoundError, jobsApi } from '@/api/jobs';
import { Button } from '@/components/ui/button';
import { formatDuration, JobField, JobOutcome, JobTime } from './presentation';

export function JobDetailsPage() {
  const { id } = useParams();
  const jobId = id && /^\d+$/.test(id) ? Number(id) : Number.NaN;
  const validId = Number.isSafeInteger(jobId) && jobId > 0;
  const [job, setJob] = useState<ReportedMonitorSummary | null>(null);
  const [runs, setRuns] = useState<ReportedRunRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [jobError, setJobError] = useState<string | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const currentRequest = ++requestId.current;
    if (!validId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setJobError(null);
    setRunsError(null);
    setNotFound(false);
    setLoadingMore(false);
    try {
      const detail = await jobsApi.get(jobId);
      if (currentRequest !== requestId.current) return;
      setJob(detail);
      try {
        const page = await jobsApi.runs(jobId);
        if (currentRequest !== requestId.current) return;
        setRuns(page.runs);
        setNextCursor(page.nextCursor);
      } catch (cause) {
        if (currentRequest !== requestId.current) return;
        setRunsError(cause instanceof Error ? cause.message : 'Could not load job runs');
      }
    } catch (cause) {
      if (currentRequest !== requestId.current) return;
      if (cause instanceof JobNotFoundError) setNotFound(true);
      else setJobError(cause instanceof Error ? cause.message : 'Could not load job');
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [jobId, validId]);

  useEffect(() => {
    setJob(null);
    setRuns([]);
    setNextCursor(null);
    void load();
    return () => {
      requestId.current++;
    };
  }, [load]);

  const loadMore = async () => {
    if (!validId || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    setRunsError(null);
    const currentRequest = requestId.current;
    try {
      const page = await jobsApi.runs(jobId, nextCursor);
      if (currentRequest !== requestId.current) return;
      setRuns((current) => {
        const identity = (run: ReportedRunRecord) => `${run.reporterId}:${run.runId}`;
        const known = new Set(current.map(identity));
        return [...current, ...page.runs.filter((run) => !known.has(identity(run)))];
      });
      setNextCursor(page.nextCursor);
    } catch (cause) {
      if (currentRequest !== requestId.current) return;
      setRunsError(cause instanceof Error ? cause.message : 'Could not load more runs');
    } finally {
      if (currentRequest === requestId.current) setLoadingMore(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <Link
        to="/dashboard/jobs"
        className="text-sm font-medium text-blue-700 hover:underline focus-visible:outline-2 focus-visible:outline-blue-600"
      >
        ← Back to Jobs
      </Link>
      {loading ? (
        <output className="mt-6 flex items-center gap-2 text-gray-600">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading job…
        </output>
      ) : notFound ? (
        <div className="mt-6 rounded-lg bg-white p-6 shadow">
          <h1 className="text-xl font-semibold">Job not found</h1>
          <p className="mt-2 text-sm text-gray-600">This job is unavailable.</p>
        </div>
      ) : jobError ? (
        <div className="mt-6 rounded-lg bg-white p-6 shadow" role="alert">
          <p className="text-sm text-red-700">{jobError}</p>
          <Button variant="outline" className="mt-4" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : job ? (
        <>
          <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900 break-words">{job.name}</h1>
              <p className="mt-1 text-sm text-gray-600">Reported job</p>
            </div>
            <Button variant="outline" onClick={() => void load()}>
              Refresh
            </Button>
          </div>
          <section
            className="mt-6 rounded-lg bg-white p-6 shadow"
            aria-labelledby="job-result-heading"
          >
            <h2 id="job-result-heading" className="text-lg font-semibold text-gray-900">
              Latest completed result
            </h2>
            <dl className="mt-5 grid gap-5 sm:grid-cols-3">
              <JobField label="Outcome">
                <JobOutcome value={job.state?.latestOutcome} />
              </JobField>
              <JobField label="Finished">
                <JobTime value={job.state?.latestFinishedAt} />
              </JobField>
              <JobField label="Last report received">
                <JobTime value={job.state?.lastReportReceivedAt} />
              </JobField>
            </dl>
          </section>
          <section
            className="mt-6 rounded-lg bg-white p-6 shadow"
            aria-labelledby="reporting-heading"
          >
            <h2 id="reporting-heading" className="text-lg font-semibold text-gray-900">
              Reporting
            </h2>
            <dl className="mt-5 grid gap-5 sm:grid-cols-3">
              <JobField label="Reporter">{job.reporter?.name ?? 'Not assigned'}</JobField>
              <JobField label="Last scan">
                {job.reporter?.scanStatus === 'ok'
                  ? 'Successful'
                  : job.reporter?.scanStatus === 'error'
                    ? 'Error'
                    : job.reporter?.scanStatus === 'starting'
                      ? 'Starting'
                      : 'Not reported'}
              </JobField>
              <JobField label="Last successful scan">
                <JobTime value={job.reporter?.lastSuccessfulScanAt} />
              </JobField>
              <JobField label="Last contact">
                <JobTime value={job.reporter?.lastContactAt} />
              </JobField>
              {job.reporter?.errorCode ? (
                <JobField label="Scan error code">{job.reporter.errorCode}</JobField>
              ) : null}
            </dl>
          </section>
          <section
            className="mt-6 overflow-hidden rounded-lg bg-white shadow"
            aria-labelledby="runs-heading"
          >
            <div className="border-b px-6 py-4">
              <h2 id="runs-heading" className="text-lg font-semibold text-gray-900">
                Recent runs
              </h2>
            </div>
            {runs.length === 0 && !runsError ? (
              <p className="px-6 py-10 text-sm text-gray-600">No runs have been reported yet.</p>
            ) : null}
            {runs.length > 0 ? (
              <div className="divide-y divide-gray-100">
                <div className="hidden grid-cols-4 gap-4 bg-gray-50 px-6 py-3 text-xs font-semibold text-gray-600 md:grid">
                  <span>Outcome</span>
                  <span>Started</span>
                  <span>Finished</span>
                  <span>Duration</span>
                </div>
                {runs.map((run) => (
                  <div
                    key={`${run.reporterId}:${run.runId}`}
                    className="grid gap-3 px-6 py-4 text-sm md:grid-cols-4 md:gap-4"
                  >
                    <div>
                      <span className="mr-2 text-xs text-gray-500 md:hidden">Outcome</span>
                      <JobOutcome value={run.outcome} />
                    </div>
                    <div>
                      <span className="mr-2 text-xs text-gray-500 md:hidden">Started</span>
                      <JobTime value={run.startedAt} />
                    </div>
                    <div>
                      <span className="mr-2 text-xs text-gray-500 md:hidden">Finished</span>
                      <JobTime value={run.finishedAt} />
                    </div>
                    <div>
                      <span className="mr-2 text-xs text-gray-500 md:hidden">Duration</span>
                      {formatDuration(run.startedAt, run.finishedAt) ?? '—'}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            {runsError ? (
              <div className="px-6 py-4" role="alert">
                <p className="text-sm text-red-700">{runsError}</p>
                <Button
                  className="mt-3"
                  variant="outline"
                  onClick={() => (nextCursor ? void loadMore() : void load())}
                >
                  Retry
                </Button>
              </div>
            ) : null}
            {nextCursor && !runsError ? (
              <div className="border-t px-6 py-4">
                <Button variant="outline" onClick={() => void loadMore()} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load more runs'}
                </Button>
              </div>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
}
